import { useEffect } from 'react'
import { Icon, type IconName } from './Icon'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastProps {
  id: string
  type: ToastType
  message: string
  duration?: number
  onDismiss: (id: string) => void
}

const icons = {
  success: 'check',
  error: 'x',
  warning: 'alert-triangle',
  info: 'info',
} as const

const colors = {
  success: 'bg-green-50 text-green-800 border-green-200',
  error: 'bg-red-50 text-red-800 border-red-200',
  warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  info: 'bg-blue-50 text-blue-800 border-blue-200',
}

export function Toast({ id, type, message, duration = 4000, onDismiss }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(id)
    }, duration)

    return () => clearTimeout(timer)
  }, [id, duration, onDismiss])

  return (
    <div
      className={`
        pointer-events-auto
        flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-lg transition-all
        animate-in slide-in-from-top-2 fade-in
        ${colors[type]}
      `}
      role="alert"
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon 
          name={icons[type] as IconName} 
          size={18} 
        />
      </div>
      <div className="flex-1 text-sm font-medium">{message}</div>
      <button
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 -mr-1 -mt-1 p-1 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <Icon name="x" size={16} />
      </button>
    </div>
  )
}
